/**
 * Service: Waitlist
 * Wraps waitlist entry CRUD and tenant waitlist queries.
 */
import { supabase } from "@/integrations/supabase/client";

export interface WaitlistEntryRow {
  id: string;
  tenant_id: string;
  user_id: string;
  card_request_id: string | null;
  email: string;
  first_name: string | null;
  last_name: string | null;
  stripe_setup_intent_id: string | null;
  payment_method_captured: boolean;
  status: string;
  position: number | null;
  joined_at: string;
  created_at: string;
  referral_code: string | null;
  referred_by: string | null;
  referral_count: number;
  referral_earnings_cents: number;
}

/* ── Reads ── */

export async function getWaitlistCount(tenantId: string) {
  // Public RPC — waitlist_entries is no longer publicly readable.
  const { data, error } = await supabase.rpc("get_waitlist_count", { _tenant_id: tenantId });
  return { count: (data as number) ?? 0, error };
}

export async function getUserWaitlistEntry(tenantId: string, userId: string) {
  return supabase
    .from("waitlist_entries")
    .select("*")
    .eq("tenant_id", tenantId)
    .eq("user_id", userId)
    .maybeSingle();
}

export async function listWaitlistEntries(tenantId: string) {
  return supabase
    .from("waitlist_entries")
    .select("*")
    .eq("tenant_id", tenantId)
    .eq("status", "active")
    .order("position");
}

export async function getWaitlistTenantInfo(tenantId: string) {
  return supabase
    .from("tenants")
    .select("waitlist_goal, waitlist_current, tenant_type")
    .eq("id", tenantId)
    .single();
}

/* ── Writes ── */

export async function joinWaitlist(payload: {
  tenant_id: string;
  user_id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  card_request_id?: string;
  referred_by?: string;
  // NOTE: stripe_setup_intent_id / payment_method_captured are server-controlled
  // (guard_waitlist_privileged_columns). Set them via verify-setup-intent only.
}) {
  return supabase.from("waitlist_entries").insert(payload as any).select().single();
}

/** Look up a waitlist entry by its referral code */
export async function getEntryByReferralCode(tenantId: string, code: string) {
  return supabase
    .from("waitlist_entries")
    .select("id, user_id, referral_code")
    .eq("tenant_id", tenantId)
    .eq("referral_code", code.toUpperCase())
    .eq("status", "active")
    .maybeSingle();
}

/* ── Entrant standing (server-derived; NEVER recompute position client-side) ── */

export interface WaitlistScoreEvent {
  id: string;
  entry_id: string;
  delta: number;
  reason: string;
  note: string | null;
  created_at: string;
}

export interface WaitlistStanding {
  entry_id: string;
  tenant_id: string;
  program_id: string | null;
  starting_score: number;
  score: number;
  score_from_log: number;
  score_matches_log: boolean;
  /** Derived rank from waitlist_ranked(). Null only if the entry is inactive. */
  position: number | null;
  referral_count: number;
  events: WaitlistScoreEvent[];
}

export interface WaitlistMove {
  from_position: number;
  to_position: number;
  places_moved: number;
  direction: "up" | "down";
  /** Cause attribution is the SERVER's: own score change vs queue shifting. */
  cause: "own_score" | "queue_shift";
  score_delta: number;
  referral_events: number;
  at: string;
}

export interface WaitlistMovement {
  entry_id: string;
  tenant_id: string;
  queue_total: number;
  moves: WaitlistMove[];
}

/**
 * Own standing: score, DERIVED position and recent score events.
 * RLS/gate: the RPC refuses any entry that is not the caller's (or staff's).
 */
export async function getWaitlistStanding(entryId: string) {
  const { data, error } = await supabase.rpc("get_waitlist_standing", { _entry_id: entryId });
  return { standing: (data as unknown as WaitlistStanding) ?? null, error };
}

/** Own movement history, cause-attributed server-side. Both directions. */
export async function getWaitlistMovement(entryId: string, limit = 5) {
  const { data, error } = await supabase.rpc("get_waitlist_movement", {
    _entry_id: entryId,
    _limit: limit,
  });
  return { movement: (data as unknown as WaitlistMovement) ?? null, error };
}

/* ── §2a Part 2 — OPERATOR QUEUE ────────────────────────────────────────────
 * Every read below is server-ordered and server-scoped. The queue position
 * comes from the same ROW_NUMBER() expression as waitlist_ranked(); it is
 * computed before filtering and pagination. NEVER re-sort or re-rank in the
 * client — a filtered page still carries true queue positions.
 * ------------------------------------------------------------------------- */

export interface WaitlistQueueRow {
  entry_id: string;
  /** DERIVED, per-tenant. Server-owned. */
  position: number;
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  score: number;
  starting_score: number;
  referral_count: number;
  referral_code: string | null;
  routing_state: "routed" | "unrouted";
  destination_type: string | null;
  destination_tenant_id: string | null;
  destination_tenant_name: string | null;
  routed_at: string | null;
  program_id: string | null;
  program_name: string | null;
  program_slug: string | null;
  card_request_id: string | null;
  payment_method_captured: boolean;
  created_at: string;
}

export interface WaitlistQueueFilters {
  tenantId?: string | null;
  routingState?: string | null;
  destinationType?: string | null;
  destinationTenantId?: string | null;
  programId?: string | null;
  search?: string | null;
  limit?: number;
  offset?: number;
}

export interface WaitlistQueuePage {
  total: number;
  rows: WaitlistQueueRow[];
  limit: number;
  offset: number;
  scope: "platform" | "tenant";
  tenants: Array<{ id: string; name: string; slug: string }>;
  programs: Array<{ id: string; name: string }>;
  destinations: Array<{ id: string; name: string }>;
}

/** One page of the queue. Staff-scoped server-side; tenant staff never see other tenants. */
export async function getWaitlistQueuePage(f: WaitlistQueueFilters = {}) {
  const { data, error } = await supabase.rpc("waitlist_queue_page", {
    _tenant_ids: f.tenantId ? [f.tenantId] : null,
    _routing_state: f.routingState ?? null,
    _destination_type: f.destinationType ?? null,
    _destination_tenant_id: f.destinationTenantId ?? null,
    _program_id: f.programId ?? null,
    _search: f.search ?? null,
    _limit: f.limit ?? 25,
    _offset: f.offset ?? 0,
  });
  return { page: (data as unknown as WaitlistQueuePage) ?? null, error };
}

export interface WaitlistDetailScoreEvent {
  id: string;
  delta: number;
  reason: string;
  note: string | null;
  actor_id: string | null;
  actor_email: string | null;
  created_at: string;
}

export interface WaitlistDetailRoutingEvent {
  id: string;
  matched_by: string;
  rule_id: string | null;
  rule_label: string | null;
  rule_priority: number | null;
  destination_type: string | null;
  destination_tenant_id: string | null;
  destination_tenant_name: string | null;
  inputs: Record<string, unknown>;
  config_version: number | null;
  evaluated_at: string;
}

export interface WaitlistDetailSnapshot {
  position: number;
  score: number;
  captured_at: string;
}

export interface WaitlistEntryDetail {
  entry: {
    entry_id: string;
    tenant_id: string;
    tenant_name: string | null;
    user_id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    status: string;
    position: number | null;
    queue_total: number;
    score: number;
    starting_score: number;
    referral_code: string | null;
    referral_count: number;
    referral_earnings_cents: number;
    program_id: string | null;
    program_name: string | null;
    card_request_id: string | null;
    payment_method_captured: boolean;
    created_at: string;
  };
  score_events: WaitlistDetailScoreEvent[];
  /** Append-only: EVERY evaluation, not just the latest. */
  routing_events: WaitlistDetailRoutingEvent[];
  /** Position over time, downward moves included. */
  rank_snapshots: WaitlistDetailSnapshot[];
}

export async function getWaitlistEntryDetail(entryId: string) {
  const { data, error } = await supabase.rpc("waitlist_entry_detail", { _entry_id: entryId });
  return { detail: (data as unknown as WaitlistEntryDetail) ?? null, error };
}

/**
 * Manual score adjustment. Operators adjust SCORE, never position.
 * The RPC requires an actor for reason='manual_adjustment' and is staff-only;
 * the note is required by THIS layer because it is what makes the adjustment
 * explainable months later.
 */
export async function adjustWaitlistScore(entryId: string, delta: number, note: string) {
  const trimmed = note.trim();
  if (!trimmed) return { result: null, error: new Error("note_required") };
  if (!Number.isInteger(delta) || delta === 0) {
    return { result: null, error: new Error("delta_must_be_nonzero_integer") };
  }
  const { data, error } = await supabase.rpc("award_waitlist_score", {
    _entry_id: entryId,
    _delta: delta,
    _reason: "manual_adjustment",
    _note: trimmed,
  });
  return {
    result: (data as unknown as { entry_id: string; score: number; position: number | null }) ?? null,
    error,
  };
}

/** PII disclosure — audited server-side (who, when, row count, filter). */
export async function recordWaitlistExport(rowCount: number, filters: Record<string, unknown>) {
  const { data, error } = await supabase.rpc("record_waitlist_export", {
    _row_count: rowCount,
    _filters: filters as never,
  });
  return { auditId: (data as string) ?? null, error };
}
